/**
 * useWorkspace Hook
 *
 * React hook for fetching current workspace data and usage stats.
 * Provides workspace info (id, name, description) and usage stats (storage, tables, members).
 *
 * Usage:
 * ```tsx
 * const { workspace, storage, tables, members, updateWorkspace } = useWorkspace();
 * ```
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/lib/queryKeys';
import { useRpc } from '@/lib/rpc';
import { useToastStore } from '@/store/useToasts';
import { useWorkspaceStore } from '@/store/useWorkspaceStore';

type WorkspaceId = string | null;

/**
 * Usage stats for a resource (storage, tables, or members)
 */
interface UsageStats {
  usage: number;
  limit: number | null;
  percent_used: number;
  available?: number | null;
}

/**
 * Workspace info from the API
 */
interface WorkspaceInfo {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Membership info from the API
 */
interface MembershipInfo {
  role: string;
  created_at: string;
}

/**
 * User info from the API
 */
interface UserInfo {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
}

/**
 * Workspace API response
 */
interface WorkspaceResponse {
  workspace: WorkspaceInfo;
  membership: MembershipInfo;
  user: UserInfo | null;
  storage: UsageStats | null;
  tables: UsageStats | null;
  members: UsageStats | null;
}

/**
 * Update workspace payload
 */
interface UpdateWorkspacePayload {
  name?: string;
  description?: string;
}

/**
 * Workspace hook for fetching workspace data and usage stats
 */
export function useWorkspace() {
  const rpc = useRpc();
  const queryClient = useQueryClient();
  const { error: showErrorToast, success: showSuccessToast } = useToastStore();
  const workspaceId: WorkspaceId = useWorkspaceStore((state) => state.selectedWorkspaceId);
  const clearSelectedWorkspace = useWorkspaceStore((state) => state.clearSelectedWorkspace);

  // Fetch workspace details
  const {
    data: workspaceData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    // Include workspace ID in key so cache is workspace-scoped
    queryKey: queryKeys.workspace.details(workspaceId!),
    queryFn: async () => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces.me.$get();
      if (!res.ok) {
        throw new Error('Failed to fetch workspace');
      }
      const data = await res.json();
      return data as WorkspaceResponse;
    },
    enabled: !!workspaceId,
    // Refetch every 5 minutes to catch usage changes
    refetchInterval: 5 * 60 * 1000,
  });

  // Update workspace mutation
  const updateMutation = useMutation({
    mutationFn: async (payload: UpdateWorkspacePayload) => {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces[':id'].$put({
        param: { id: workspaceId },
        json: payload,
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error((errorData as { error?: string }).error || 'Failed to update workspace');
      }
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workspace.detailsBase });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workspaces.all });
      showSuccessToast('Workspace Updated', 'Workspace settings have been saved');
    },
    onError: (err) => {
      showErrorToast(
        'Update Error',
        err instanceof Error ? err.message : 'Failed to update workspace'
      );
    },
  });

  /**
   * Update workspace name and/or description
   */
  const updateWorkspace = (payload: UpdateWorkspacePayload) => {
    updateMutation.mutate(payload);
  };

  // Delete workspace mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces[':id'].$delete({
        param: { id: workspaceId },
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error((errorData as { error?: string }).error || 'Failed to delete workspace');
      }
      return res.json();
    },
    onSuccess: () => {
      // Clear workspace from store
      clearSelectedWorkspace();
      // Invalidate workspaces list
      void queryClient.invalidateQueries({ queryKey: queryKeys.workspaces.all });
      showSuccessToast('Workspace Deleted', 'The workspace has been permanently deleted');
    },
    onError: (err) => {
      showErrorToast(
        'Delete Error',
        err instanceof Error ? err.message : 'Failed to delete workspace'
      );
    },
  });

  /**
   * Delete the workspace permanently (owner only)
   */
  const deleteWorkspace = () => {
    deleteMutation.mutate();
  };

  return {
    // Workspace info
    workspace: workspaceData?.workspace ?? null,
    membership: workspaceData?.membership ?? null,
    user: workspaceData?.user ?? null,

    // Usage stats
    storage: workspaceData?.storage ?? null,
    tables: workspaceData?.tables ?? null,
    members: workspaceData?.members ?? null,

    // Query state
    isLoading,
    error,
    refetch,

    // Update mutation
    updateWorkspace,
    isUpdating: updateMutation.isPending,

    // Delete mutation
    deleteWorkspace,
    isDeleting: deleteMutation.isPending,
  };
}
